import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { 
  Shield, 
  Search, 
  Filter, 
  Ban, 
  AlertTriangle, 
  Clock,
  CheckCircle,
  X,
  User,
  Calendar,
  MessageSquare,
  Eye,
  Settings,
  FileText,
  UserX,
  RotateCcw
} from 'lucide-react';

interface Player {
  id: string;
  displayName: string;
  email: string;
  photoURL?: string;
  status: 'active' | 'suspended' | 'banned';
  registeredAt: string;
  lastActive: string;
  totalGames: number;
  reportCount: number;
}

interface Report {
  id: string;
  reporterId: string;
  reporterName: string;
  reportedPlayerId: string;
  reportedPlayerName: string;
  type: 'cheating' | 'harassment' | 'inappropriate_content' | 'other';
  description: string;
  evidence?: string;
  status: 'pending' | 'investigating' | 'resolved' | 'dismissed';
  severity: 'low' | 'medium' | 'high' | 'critical';
  createdAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
  actionTaken?: string;
}

interface Punishment {
  id: string;
  playerId: string;
  playerName: string;
  type: 'warning' | 'temporary_suspension' | 'permanent_ban';
  reason: string;
  duration?: number; // days
  issuedBy: string;
  issuedAt: string;
  expiresAt?: string;
  isActive: boolean;
  notes?: string;
}

const Moderation: React.FC = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'reports' | 'punishments' | 'players'>('reports');
  const [reports, setReports] = useState<Report[]>([]);
  const [punishments, setPunishments] = useState<Punishment[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [showPunishmentModal, setShowPunishmentModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [loading, setLoading] = useState(false);

  // Firebase data loading
  useEffect(() => {
    // Load reports from Firestore
    const unsubscribeReports = onSnapshot(
      collection(db, 'moderation_reports'), 
      (snapshot) => {
        const reportsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || doc.data().createdAt,
          resolvedAt: doc.data().resolvedAt?.toDate?.()?.toISOString() || doc.data().resolvedAt
        })) as Report[];
        reportsData.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setReports(reportsData);
      },
      (error) => {
        console.error('Error fetching moderation reports:', error);
        if (error.code !== 'permission-denied') {
          toast.error('Hiba történt a jelentések betöltésekor');
        }
      }
    );

    // Load punishments from Firestore
    const unsubscribePunishments = onSnapshot(
      collection(db, 'moderation_punishments'), 
      (snapshot) => {
        const punishmentsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          issuedAt: doc.data().issuedAt?.toDate?.()?.toISOString() || doc.data().issuedAt,
          expiresAt: doc.data().expiresAt?.toDate?.()?.toISOString() || doc.data().expiresAt
        })) as Punishment[];
        punishmentsData.sort((a, b) => new Date(b.issuedAt).getTime() - new Date(a.issuedAt).getTime());
        setPunishments(punishmentsData);
      },
      (error) => {
        console.error('Error fetching moderation punishments:', error);
        if (error.code !== 'permission-denied') {
          toast.error('Hiba történt a büntetések betöltésekor');
        }
      }
    );

    // Load players from Firestore (from 'users' collection)
    const unsubscribePlayers = onSnapshot(
      collection(db, 'users'), 
      (snapshot) => {
        const playersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          registeredAt: doc.data().registeredAt?.toDate?.()?.toISOString() || doc.data().registeredAt,
          lastActive: doc.data().lastActive?.toDate?.()?.toISOString() || doc.data().lastActive
        })) as Player[];
        setPlayers(playersData);
      },
      (error) => {
        console.error('Error fetching players:', error);
        if (error.code !== 'permission-denied') {
          toast.error('Hiba történt a játékosok betöltésekor');
        }
      }
    );

    return () => {
      try {
        unsubscribeReports();
        unsubscribePunishments();
        unsubscribePlayers();
      } catch (error) {
        console.error('Error cleaning up moderation subscriptions:', error);
      }
    };
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'investigating': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'resolved': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'dismissed': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'suspended': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'banned': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low': return 'bg-gray-100 text-gray-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('hu-HU');
  };

  const handleReportStatusChange = async (reportId: string, newStatus: string) => {
    try {
      await updateDoc(doc(db, 'moderation_reports', reportId), {
        status: newStatus,
        resolvedAt: newStatus === 'resolved' || newStatus === 'dismissed' ? serverTimestamp() : null,
        resolvedBy: newStatus === 'resolved' || newStatus === 'dismissed' ? currentUser?.displayName || currentUser?.email || 'Admin' : null
      });
      toast.success('Jelentés státusza frissítve!');
    } catch (error) {
      console.error('Hiba a státusz frissítésekor:', error);
      toast.error('Hiba történt a státusz frissítésekor');
    }
  };

  const handleIssuePunishment = async (playerId: string, type: string, reason: string, duration?: number) => {
    const player = players.find(p => p.id === playerId);
    if (!player) {
      toast.error('Játékos nem található');
      return;
    }

    setLoading(true);
    try {
      const newPunishment: Omit<Punishment, 'id'> = {
        playerId,
        playerName: player.displayName,
        type: type as any,
        reason,
        duration,
        issuedBy: currentUser?.displayName || currentUser?.email || 'Admin',
        issuedAt: new Date().toISOString(),
        expiresAt: duration ? new Date(Date.now() + duration * 24 * 60 * 60 * 1000).toISOString() : undefined,
        isActive: true
      };

      await addDoc(collection(db, 'moderation_punishments'), newPunishment);
      
      // Update player status
      const newStatus = type === 'permanent_ban' ? 'banned' : type === 'temporary_suspension' ? 'suspended' : 'active';
      await updateDoc(doc(db, 'users', playerId), {
        status: newStatus
      });

      setShowPunishmentModal(false);
      setSelectedPlayer(null);
      toast.success('Büntetés sikeresen kiadva!');
    } catch (error) {
      console.error('Hiba a büntetés kiadásakor:', error);
      toast.error('Hiba történt a büntetés kiadásakor');
    } finally {
      setLoading(false);
    }
  };

  const pendingReports = reports.filter(r => r.status === 'pending').length;
  const activeInvestigations = reports.filter(r => r.status === 'investigating').length;
  const activePunishments = punishments.filter(p => p.isActive).length;
  const suspendedPlayers = players.filter(p => p.status === 'suspended').length;

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              Moderáció
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Játékos jelentések és fegyelmi intézkedések kezelése
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Függő jelentések</p>
              <p className="text-2xl font-bold text-yellow-600">{pendingReports}</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Vizsgálat alatt</p>
              <p className="text-2xl font-bold text-blue-600">{activeInvestigations}</p>
            </div>
            <Eye className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Aktív büntetések</p>
              <p className="text-2xl font-bold text-red-600">{activePunishments}</p>
            </div>
            <Ban className="h-8 w-8 text-red-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Felfüggesztett játékosok</p>
              <p className="text-2xl font-bold text-orange-600">{suspendedPlayers}</p>
            </div>
            <UserX className="h-8 w-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('reports')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'reports'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            Jelentések ({reports.length})
          </button>
          <button
            onClick={() => setActiveTab('punishments')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'punishments'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            Büntetések ({punishments.length})
          </button>
          <button
            onClick={() => setActiveTab('players')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'players'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            Játékosok ({players.length})
          </button>
        </nav>
      </div>

      {/* Search and Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Keresés..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="all">Minden státusz</option>
            {activeTab === 'reports' && (
              <>
                <option value="pending">Függő</option>
                <option value="investigating">Vizsgálat alatt</option>
                <option value="resolved">Megoldva</option>
                <option value="dismissed">Elutasítva</option>
              </>
            )}
            {activeTab === 'players' && (
              <>
                <option value="active">Aktív</option>
                <option value="suspended">Felfüggesztve</option>
                <option value="banned">Kitiltva</option>
              </>
            )}
          </select>
        </div>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'reports' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Jelentés
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Típus
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Súlyosság
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Státusz
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Dátum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Műveletek
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {reports.filter(report => 
                  statusFilter === 'all' || report.status === statusFilter
                ).map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {report.reporterName} jelentette: {report.reportedPlayerName}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                          {report.description}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {report.type === 'cheating' ? 'Csalás' :
                         report.type === 'harassment' ? 'Zaklatás' :
                         report.type === 'inappropriate_content' ? 'Nem megfelelő tartalom' : 'Egyéb'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(report.severity)}`}>
                        {report.severity === 'low' ? 'Alacsony' :
                         report.severity === 'medium' ? 'Közepes' :
                         report.severity === 'high' ? 'Magas' : 'Kritikus'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(report.status)}`}>
                        {report.status === 'pending' ? 'Függő' :
                         report.status === 'investigating' ? 'Vizsgálat' :
                         report.status === 'resolved' ? 'Megoldva' : 'Elutasítva'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(report.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setSelectedReport(report)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {report.status === 'pending' && (
                          <button
                            onClick={() => handleReportStatusChange(report.id, 'investigating')}
                            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'punishments' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Játékos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Típus
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Indok
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Időtartam
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Kiadva
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Státusz
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {punishments.map((punishment) => (
                  <tr key={punishment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {punishment.playerName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        punishment.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                        punishment.type === 'temporary_suspension' ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {punishment.type === 'warning' ? 'Figyelmeztetés' :
                         punishment.type === 'temporary_suspension' ? 'Ideiglenes felfüggesztés' :
                         'Végleges kitiltás'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                        {punishment.reason}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {punishment.duration ? `${punishment.duration} nap` : 'Végleges'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(punishment.issuedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        punishment.isActive ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {punishment.isActive ? 'Aktív' : 'Lejárt'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'players' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Játékos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Státusz
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Játékok száma
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Jelentések
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Utolsó aktivitás
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Műveletek
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {players.filter(player => 
                  statusFilter === 'all' || player.status === statusFilter
                ).map((player) => (
                  <tr key={player.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <img
                          className="h-10 w-10 rounded-full object-cover"
                          src={player.photoURL || 'https://via.placeholder.com/40'}
                          alt=""
                        />
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {player.displayName}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {player.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(player.status)}`}>
                        {player.status === 'active' ? 'Aktív' :
                         player.status === 'suspended' ? 'Felfüggesztve' : 'Kitiltva'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {player.totalGames}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        player.reportCount === 0 ? 'bg-green-100 text-green-800' :
                        player.reportCount < 3 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {player.reportCount}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(player.lastActive)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setSelectedPlayer(player);
                            setShowPunishmentModal(true);
                          }}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <Ban className="h-4 w-4" />
                        </button>
                        {player.status !== 'active' && (
                          <button
                            onClick={() => {
                              setPlayers(prev => prev.map(p => 
                                p.id === player.id ? { ...p, status: 'active' } : p
                              ));
                            }}
                            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Punishment Modal */}
      {showPunishmentModal && selectedPlayer && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Büntetés kiadása: {selectedPlayer.displayName}
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Büntetés típusa
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white">
                      <option value="warning">Figyelmeztetés</option>
                      <option value="temporary_suspension">Ideiglenes felfüggesztés</option>
                      <option value="permanent_ban">Végleges kitiltás</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Indoklás
                    </label>
                    <textarea
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Írja be a büntetés indokát..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Időtartam (napok)
                    </label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Csak ideiglenes felfüggesztésnél"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => handleIssuePunishment(selectedPlayer.id, 'warning', 'Szabályszegés', undefined)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Büntetés kiadása
                </button>
                <button
                  onClick={() => {
                    setShowPunishmentModal(false);
                    setSelectedPlayer(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Mégse
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Moderation; 